import React, { useState } from 'react';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { submitEnquiry, enquiryError } from '../lib/enquiries';

const EMPTY = { name: '', phone: '', city: '' };
const FIELDS = [
  { key: 'name', label: 'Full Name', type: 'text', auto: 'name' },
  { key: 'phone', label: 'Phone Number', type: 'tel', auto: 'tel' },
  { key: 'city', label: 'City', type: 'text', auto: 'address-level2' },
];

const DistributorDialog = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.city.trim()) {
      toast({ title: 'Missing details', description: 'Please fill in your name, phone and city.', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await submitEnquiry('distributor', form);
      toast({ title: 'Application received', description: 'Our partnerships team will call you within 24 hours.' });
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Could not submit', description: enquiryError(err), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="distributor-dialog" className="sm:max-w-[460px] rounded-none border-0 border-t-2 border-[#c4a24a] bg-white p-8 sm:p-10">
        <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] uppercase">Grow With Tropic Blue®</div>
        <DialogTitle className="text-[28px] leading-[1.1] font-medium text-[#0f2c6b] -mt-1" style={{ fontFamily: 'Fraunces, serif' }}>
          Become a <span className="italic text-[#c4a24a] font-light">distributor.</span>
        </DialogTitle>
        <DialogDescription className="text-[13.5px] text-slate-600 font-light leading-relaxed">
          Share a few details and our partnerships team will reach out.
        </DialogDescription>
        <form onSubmit={handleSubmit} className="mt-2 space-y-6" data-testid="distributor-form">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="text-[10px] tracking-[0.25em] text-[#c4a24a] uppercase">{f.label}</span>
              <input
                data-testid={`distributor-${f.key}-input`}
                type={f.type}
                autoComplete={f.auto}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="mt-1 w-full bg-transparent border-b border-slate-300 focus:border-[#0f2c6b] focus:outline-none py-2 text-[14px] text-slate-800 transition-colors duration-300"
              />
            </label>
          ))}
          <button
            type="submit"
            disabled={sending}
            data-testid="distributor-submit-button"
            className="premium-button group w-full bg-[#0f2c6b] hover:bg-[#1e3a8a] disabled:opacity-70 text-white text-[11.5px] font-medium tracking-[0.22em] px-6 py-4 flex items-center justify-center gap-3 transition-colors duration-300"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : null}
            {sending ? 'SUBMITTING' : 'SUBMIT APPLICATION'}
            {!sending && <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform duration-300" />}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DistributorDialog;
